import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getCloudProgress } from "../utils/progressCloud";
import { DeviceSnapshot, Edit, readDeviceProgress, saveDeviceEdits, seedDeviceProgress, StudyData, subscribeDeviceProgress } from "../utils/deviceProgress";
import { plainRecord } from "../utils/progressData";
import { ProgressSyncStatus } from "./progressTypes";

export function useDeviceProgress<T extends StudyData>(feature: string, guestEditable = false) {
    const { status, user } = useAuth();
    const owner = status === "authenticated" ? user?.identityId : status === "unauthenticated" && guestEditable ? "guest" : undefined;
    const identity = useRef(owner);
    identity.current = owner;
    const generation = useRef(0);
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState<{ owner?: string; snapshot: DeviceSnapshot; status: ProgressSyncStatus }>({ snapshot: {}, status: "loading" });
    const currentState = useRef(state);
    currentState.current = state;
    useEffect(() => {
        const run = ++generation.current;
        if (!owner) return;
        const active = () => generation.current === run && identity.current === owner;
        const accept = (snapshot: DeviceSnapshot) => {
            if (!active()) return;
            setState(previous => previous.owner === owner && (previous.snapshot.record?.revision || 0) > (snapshot.record?.revision || 0) ? previous : {
                owner, snapshot, status: snapshot.pending ? "pending" : owner === "guest" ? "device" : "ready",
            });
        };
        const refresh = () => readDeviceProgress(owner, feature).then(accept).catch(() => {
            if (active()) setState(previous => ({ ...previous, owner, status: "error" }));
        });
        const unsubscribe = subscribeDeviceProgress((userId, changedFeature) => {
            if (userId === owner && changedFeature === feature) void refresh();
        });
        const retryDevice = (event: Event) => {
            if ((event as CustomEvent).detail === owner) setAttempt(value => value + 1);
        };
        window.addEventListener("nihongotech-device-retry", retryDevice);
        void (async () => {
            try {
                const local = await readDeviceProgress(owner, feature);
                if (!active()) return;
                if (local.record || owner === "guest" || navigator.onLine === false) accept(local);
                // The global sync coordinator reconciles retained queues.
                // Feature hydration must never replace pending edits.
                if (owner === "guest" || local.pending || navigator.onLine === false) return;
                try {
                    const remote = await getCloudProgress(owner, feature);
                    if (!active()) return;
                    const seeded = await seedDeviceProgress(owner, feature, remote ? plainRecord(feature, remote.data) : local.record?.data || {}, local.record?.revision || 0, remote?.updatedAt);
                    if (!active()) return;
                    accept(seeded);
                } catch {
                    // Device work is still available; new edits are queued.
                    if (active()) { accept(await readDeviceProgress(owner, feature)); }
                }
            } catch {
                if (active()) setState({ owner, snapshot: {}, status: "error" });
            }
        })();
        // Retire all asynchronous work from this identity/effect generation.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => { generation.current++; unsubscribe(); window.removeEventListener("nihongotech-device-retry", retryDevice); };
    }, [owner, feature, attempt]);

    const update = useCallback(async (edit: Edit | Edit[]): Promise<boolean> => {
        if (!owner || identity.current !== owner || currentState.current.owner !== owner || currentState.current.status === "error") return false;
        const run = generation.current;
        const active = () => identity.current === owner && generation.current === run;
        try {
            const saved = await saveDeviceEdits(owner, feature, Array.isArray(edit) ? edit : [edit]);
            if (!active()) return false;
            setState({ owner, snapshot: saved, status: saved.pending ? "pending" : "device" });
            return true;
        } catch {
            if (active()) {
                setState(previous => ({ ...previous, owner, status: "error" }));
                window.dispatchEvent(new CustomEvent("nihongotech-device-save-error", { detail: owner }));
            }
            return false;
        }
    }, [owner, feature]);
    const visible = Boolean(owner) && state.owner === owner;
    return {
        data: (visible ? state.snapshot.record?.data || {} : {}) as T,
        syncStatus: visible ? state.status : owner || status === "loading" ? "loading" : "signedOut" as ProgressSyncStatus,
        ready: visible && state.status !== "error", update,
        retry: useCallback(() => setAttempt(value => value + 1), []),
    };
}
